import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useContext } from 'react';
import { useAtom } from 'jotai';
import { useTranslations } from 'use-intl';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderBackButton,
  HeaderButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import {
  Sidenav,
  SidenavButton,
  SidenavEmpty,
  SidenavHeader,
  SidenavHeading,
  SidenavItem,
  SidenavList,
} from '@/app/ui/components/sidenav';
import { HugeiconsIcon } from '@hugeicons/react';
import { FileBookmarkIcon, MoreHorizontalIcon, PencilEdit02Icon } from '@hugeicons/core-free-icons';
import { Card } from '@/app/ui/components/card';
import {
  useGetExtractionTemplateList,
  useGetExtractionTemplate,
} from '@/app/hooks/queries/useExtractionsTemplates';
import { AppContext } from '@/app/appContext';
import { Spinner } from '@/app/ui/components/spinner';
import { Badge } from '@/app/ui/components/badge';
import { ExtractionTemplateOwnershipEnum, dateFormats } from '@/app/utils/constants';
import { format } from 'date-fns';
import TemplateActionsPopup from '@/app/components/features/organizations/TemplateActionsPopup';
import { MainScrollArea } from '@/app/ui/components/main';
import { Menu, MenuTrigger } from '@/app/ui/components/menu';
import { Button } from '@/app/ui/components/button';
import EditOrganizationExtractionDialog from '@/app/components/features/evaluations/CreateForm/EditOrganizationExtractionDialog';
import { editOrganizationExtractionOpenAtom } from '@/app/components/features/organizations/extractions/store';

export const Route = createFileRoute('/_authed/(organization)/_layout/templates/$templateId')({
  component: RouteComponent,
});

function RouteComponent() {
  const { templateId } = Route.useParams();
  const t = useTranslations('pages.templates');
  const { userMeta } = useContext(AppContext);
  const organizationId = userMeta.organizationId ?? '';
  const [selectedExtractionId, setSelectedExtractionId] = useState<string | null>(null);
  const [_, setEditDialogOpen] = useAtom(editOrganizationExtractionOpenAtom);

  const { data: templateData, isLoading: isLoadingTemplate } = useGetExtractionTemplateList({
    organizationId,
    customTopicTemplateListId: templateId,
    enabled: !!organizationId,
  });

  const { data: extractionDetails, isLoading: isLoadingExtraction } = useGetExtractionTemplate({
    organizationId,
    customTopicTemplateId: selectedExtractionId!,
    enabled: !!selectedExtractionId && !!organizationId,
  });

  useEffect(() => {
    if (templateData?.templates && templateData.templates.length > 0 && !selectedExtractionId) {
      setSelectedExtractionId(templateData.templates[0].customTopicTemplateId);
    }
  }, [templateData, selectedExtractionId]);

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink to={'/templates'}>
                <HugeiconsIcon icon={FileBookmarkIcon} />
                {t('breadcrumbs.templates')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            {templateData && (
              <>
                <BreadcrumbSeparator className="max-md:hidden" />
                <BreadcrumbItem>
                  <BreadcrumbPage>{templateData.name}</BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
        {templateData && (
          <Menu>
            <MenuTrigger
              render={
                <HeaderButton className="md:inline-flex" aria-label="More options">
                  <HugeiconsIcon icon={MoreHorizontalIcon} className="size-5.5 md:size-5" />
                </HeaderButton>
              }
            />
            <TemplateActionsPopup
              template={templateData}
              organizationId={organizationId}
              align="start"
            />
          </Menu>
        )}
      </Header>
      <MainScrollArea viewportClassName="lg:flex grow">
        <Sidenav className="border-r">
          <SidenavHeader>
            <SidenavHeading>
              {t('details.extractions')}{' '}
              <strong className="text-foreground">{templateData?.templates?.length ?? 0}</strong>
            </SidenavHeading>
          </SidenavHeader>
          <SidenavList>
            {isLoadingTemplate ? (
              <Spinner kind="inset" />
            ) : !templateData?.templates || templateData.templates.length === 0 ? (
              <SidenavEmpty>{t('details.noExtractions')}</SidenavEmpty>
            ) : (
              templateData.templates.map((extraction, index) => (
                <SidenavItem key={extraction.customTopicTemplateId}>
                  <SidenavButton
                    active={selectedExtractionId === extraction.customTopicTemplateId}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedExtractionId(extraction.customTopicTemplateId);
                    }}
                  >
                    <span className="line-clamp-1">
                      <span className="w-5">{index + 1}.</span> {extraction.userQuery}
                    </span>
                  </SidenavButton>
                </SidenavItem>
              ))
            )}
          </SidenavList>
        </Sidenav>
        <div className="grow flex flex-col min-w-0 gap-4 p-4 bg-surface-2">
          {isLoadingExtraction ? (
            <Card className="grow p-12 flex items-center justify-center">
              <Spinner kind="inset" />
            </Card>
          ) : extractionDetails ? (
            <Card className="grow p-6 overflow-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">{extractionDetails.userQuery}</h3>
                <Button variant="secondary" kind="icon" onClick={() => setEditDialogOpen(true)}>
                  <HugeiconsIcon icon={PencilEdit02Icon} />
                </Button>
              </div>
              <div className="space-y-4">
                {extractionDetails.owner === ExtractionTemplateOwnershipEnum.VOLVE ? (
                  <div className="flex gap-2 items-center">
                    <span className="text-sm text-muted">{t('details.createdBy')}</span>
                    <Badge size="sm">Volve</Badge>
                  </div>
                ) : (
                  <div className="flex gap-1 text-sm">
                    <span className="text-muted">{t('details.createdBy')}</span>
                    <span className="font-medium">{extractionDetails.userDisplayName}</span>
                    {extractionDetails.createdAt && (
                      <span>({format(extractionDetails.createdAt, dateFormats.dayFirst)})</span>
                    )}
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted font-medium mb-3">{t('details.queries')}</p>
                  <ol className="list-decimal pl-4 space-y-3">
                    {extractionDetails.queries?.map((query) => (
                      <li key={query.id} className="text-sm pl-2">
                        <div className="flex flex-col gap-2">
                          <p className="font-medium text-foreground">{query.name}</p>
                          <p className="text-muted">{query.userQuery}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </Card>
          ) : (
            <Card className="grow p-12 text-center text-muted">
              {t('details.selectExtraction')}
            </Card>
          )}
        </div>
        {extractionDetails && (
          <EditOrganizationExtractionDialog
            extractionTemplate={{
              id: extractionDetails.id,
              title: extractionDetails.userQuery,
              queries:
                extractionDetails.queries?.map((q) => ({
                  id: q.id,
                  name: q.name,
                  userQuery: q.userQuery,
                })) || [],
            }}
          />
        )}
      </MainScrollArea>
    </>
  );
}
